import React, {useMemo, useCallback} from 'react';
import {MarketplaceOnly} from '@udemy-v2/react-brand-components';
import {Icon} from '@udemy-v2/icons';
import {Button, IconButton} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {generateTrackingId, publishEvent} from '@udemy-v2/event-tracking';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useIsLoggedIn} from '@/lib/hooks/use-is-logged-in';
import {
    GiftBuyablesStartEvent,
    LearningProduct,
} from '@/features/course-landing-page/events/events';
import {META_DEFAULT_BASE_URL} from '@/app/[lang]/constants';

import classNames from 'classnames';
import styles from './gift-this-course.module.scss';

/**
 * Extracts course slug from course object
 * @param course - The course object
 * @returns Course slug or course ID as fallback
 */
function extractCourseSlug(course: {urlCourseLanding?: string; id: string}): string {
    if (!course.urlCourseLanding) return course.id;

    try {
        const url = new URL(course.urlCourseLanding);
        const pathParts = url.pathname.split('/').filter(Boolean);
        // URL format: /course/{slug}/ or /course/{slug}
        if (pathParts.length >= 2 && pathParts[0] === 'course') {
            return pathParts[1];
        }
    } catch {
        // If URL parsing fails, fall back to course ID
    }

    return course.id;
}

/**
 * Gift This Course Button Component
 *
 * Renders a button to gift the course to someone else.
 * Only shows for paid courses (not free courses).
 * Wrapped in MarketplaceOnly component to only show on marketplace.
 */
export const GiftThisCourse = ({
    onlyIcon = false,
    className,
}: {
    onlyIcon?: boolean;
    className?: string;
}) => {
    const {gettext, locale} = useI18n();
    const course = useClpCourse();
    const isLoggedIn = useIsLoggedIn();

    // Extract course slug from course object
    const courseSlug = useMemo(() => {
        return extractCourseSlug(course);
    }, [course]);

    // Construct the gift course URL based on login status
    const giftCourseUrl = useMemo(() => {
        if (isLoggedIn) {
            // Logged in user: direct to gift page
            return `/gift/${courseSlug}/`;
        } else {
            // Not logged in: redirect to signup with return link
            const params = new URLSearchParams({
                locale: locale || 'en_US',
                response_type: 'html',
                return_link:
                    course.urlCourseLanding || `${META_DEFAULT_BASE_URL}/course/${courseSlug}/`,
                source: 'course_landing_page',
                ref: '',
                next: `${META_DEFAULT_BASE_URL}/gift/${courseSlug}/`,
            });
            // TODO: export a function to get the signup popup url from frontends-components-v2
            return `/join/signup-popup/?${params.toString()}`;
        }
    }, [isLoggedIn, courseSlug, locale, course.urlCourseLanding]);

    // Handle gift button click with event tracking
    const handleGiftClick = useCallback(() => {
        // Create buyable item from course data
        const buyableItem = {
            id: Number(course.id),
            type: LearningProduct.COURSE,
            trackingId: generateTrackingId(),
        };

        // Publish the gift flow start event
        publishEvent(
            new GiftBuyablesStartEvent([buyableItem], 'sidebar.purchase_section.course_actions'),
        );
    }, [course.id]);

    // Don't render if course is free
    // Note: We intentionally allow gifting even when there's a 100% discount coupon applied
    if (course.isFree) {
        return null;
    }

    return (
        <MarketplaceOnly>
            {onlyIcon ? (
                <IconButton
                    componentClass="a"
                    href={giftCourseUrl}
                    udStyle="ghost"
                    size="large"
                    className={classNames(
                        styles['gift-button'],
                        'ud-link-underline',
                        'ud-link-neutral',
                        className,
                    )}
                    data-purpose="gift-this-course"
                    onClick={handleGiftClick}
                    aria-label={gettext('Gift this course')}
                >
                    <Icon label={false} glyph="gift" aria-hidden="true" />
                </IconButton>
            ) : (
                <Button
                    componentClass="a"
                    href={giftCourseUrl}
                    udStyle="ghost"
                    className={classNames(
                        styles['gift-button'],
                        'ud-link-underline',
                        'ud-link-neutral',
                        className,
                    )}
                    data-purpose="gift-this-course"
                    onClick={handleGiftClick}
                >
                    {gettext('Gift this course')}
                </Button>
            )}
        </MarketplaceOnly>
    );
};
